import type { ConsentInfo } from '../../../../definitions/consent-info';

/** The parts of `/consent/info` that describe the sites a user can grant. */
export type AvailableSitesInfo = Pick<
  ConsentInfo,
  'availableSites' | 'availableWorkspaces'
>;

/**
 * A site the user can consent to, as the selectors need it: the URL is both the
 * display value and what goes on the wire as `selectedWorkspace`. The ARI is
 * deliberately absent — a site has one per product, so it isn't a stable key
 * for the selection.
 */
export type ConsentSite = {
  url: string;
  avatar?: string;
  /**
   * Display name resolved by the backend, when it sends one. Only some products
   * do — a loom workspace URL is a cloud id rather than a readable host, so this
   * is the only readable label it has. Absent means the selector derives a name
   * from the URL instead (see deriveSiteNameFromUrl).
   */
  name?: string;
};

/**
 * Sites the user can consent to, in the order `/consent/info` returned them.
 *
 * `availableSites` has one entry per product ARI, so a single site shows up
 * several times (e.g. `ari:cloud:jira::site/<id>` and
 * `ari:cloud:confluence::site/<id>` both carry `url: "pug.jira-dev.com"`).
 * Consent is granted per site, so entries are deduplicated by URL, keeping the
 * first entry's avatar and name.
 *
 * Falls back to the flat `availableWorkspaces` list when `availableSites` is
 * absent or carries no usable URL, so the flow keeps working against responses
 * from before `availableSites` was added, and for flows whose backend does not
 * populate it yet. Those sites have no avatar and no name.
 */
export function getAvailableConsentSites(
  info: AvailableSitesInfo,
): ConsentSite[] {
  const sitesByUrl = new Map<string, ConsentSite>();

  // Tolerates a missing URL: these come straight off the /consent/info payload,
  // so the declared types are only as good as the response.
  const addSite = (
    rawUrl: string | null | undefined,
    avatar?: string,
    rawName?: string,
  ) => {
    const url = rawUrl?.trim();
    if (url && !sitesByUrl.has(url)) {
      const name = rawName?.trim();
      sitesByUrl.set(url, {
        url,
        ...(avatar ? { avatar } : {}),
        ...(name ? { name } : {}),
      });
    }
  };

  for (const site of info.availableSites ?? []) {
    addSite(site.url, site.avatar, site.name);
  }

  if (sitesByUrl.size > 0) {
    return [...sitesByUrl.values()];
  }

  for (const workspace of info.availableWorkspaces ?? []) {
    addSite(workspace);
  }

  return [...sitesByUrl.values()];
}

/**
 * Human-friendly display name for a site URL: the first DNS label, capitalised
 * — `mario.atlassian.net` -> `Mario`. `/consent/info` names only some sites, so
 * the selector derives one for the rest. Falls back to the URL itself when there
 * is no recognisable label.
 */
export function deriveSiteNameFromUrl(url: string): string {
  const firstLabel = url.split('.')[0]?.trim();
  if (!firstLabel) {
    return url;
  }
  return firstLabel.charAt(0).toUpperCase() + firstLabel.slice(1);
}

/**
 * The selected site URLs held in consent context's `selectedWorkspace`.
 *
 * Several sites are stored comma-joined, as the Rovo and Kaizen site selectors
 * do, so the single-site flows and the other `selectedWorkspace` writers (the
 * bypass banner, the Trello-only hidden site) keep working unchanged: a single
 * URL never contains a comma and parses to itself.
 */
export function parseSelectedSiteUrls(
  selectedWorkspace: string | null | undefined,
): string[] {
  return (
    selectedWorkspace
      ?.split(',')
      .map((url) => url.trim())
      .filter(Boolean) ?? []
  );
}

/** The inverse of parseSelectedSiteUrls; null when nothing is selected. */
export function serializeSelectedSiteUrls(
  urls: readonly string[],
): string | null {
  const unique = [...new Set(urls.filter(Boolean))];
  return unique.length > 0 ? unique.join(',') : null;
}
